import React, { useEffect, useState } from "react";
import { Card, Switch, Tag, Spin } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import GetSecuritySettingsApi from "../../api/security-settings/GetSecuritySettingsApi";
import UpdateSecuritySettingsApi from "../../api/security-settings/UpdateSecuritySettingsApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";

// The 6 CSE password-policy / 2FA features. Each maps to a backend flag.
const FEATURES = [
  {
    key: "passwordLengthEnabled",
    title: "Password Length",
    policy: "CSE Password Policy - 12 characters",
    description: "Require passwords to be at least 12 characters long.",
  },
  {
    key: "passwordComplexityEnabled",
    title: "Password Complexity",
    policy: "CSE Password Policy - Complexity enabled",
    description:
      "Require a mix of uppercase, lowercase, and a number or special character.",
  },
  {
    key: "passwordExpiryEnabled",
    title: "Password Expiry",
    policy: "CSE Password Policy - 60 days",
    description: "Force users to change their password every 60 days.",
  },
  {
    key: "accountLockoutEnabled",
    title: "Account Lockout",
    policy: "CSE Password Policy - 3 failed attempts",
    description: "Lock an account after 3 consecutive failed login attempts.",
  },
  {
    key: "passwordHistoryEnabled",
    title: "Password History",
    policy: "CSE Password Policy - remember 5 passwords",
    description: "Prevent reuse of the last 5 passwords.",
  },
  {
    key: "twoFactorEnabled",
    title: "Two-Factor Authentication (2FA)",
    policy: "Email OTP on login",
    description: "Require CSE users to verify a one-time code on every login.",
  },
];

const DEFAULT_SETTINGS = {
  passwordLengthEnabled: false,
  passwordComplexityEnabled: false,
  passwordExpiryEnabled: false,
  accountLockoutEnabled: false,
  passwordHistoryEnabled: false,
  twoFactorEnabled: false,
};

function SecuritySettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await GetSecuritySettingsApi();
      if (response?.success && response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      } else {
        openNotificationError(
          "bottomRight",
          response?.message || "Failed to load security settings"
        );
      }
    } catch (error) {
      openNotificationError("bottomRight", "Error loading security settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, enabled) => {
    setUpdating((prev) => ({ ...prev, [key]: true }));

    // Optimistic UI update
    const previous = settings[key];
    setSettings((prev) => ({ ...prev, [key]: enabled }));

    try {
      const response = await UpdateSecuritySettingsApi({ [key]: enabled });
      if (response?.success && response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
        openNotificationSuccess(
          "bottomRight",
          "Security setting updated successfully"
        );
      } else {
        // revert on failure
        setSettings((prev) => ({ ...prev, [key]: previous }));
        openNotificationError(
          "bottomRight",
          response?.message || "Failed to update security setting"
        );
      }
    } catch (error) {
      setSettings((prev) => ({ ...prev, [key]: previous }));
      openNotificationError("bottomRight", "Error updating security setting");
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <div className="p-6">
      <Card>
        <div className="mb-6 flex items-center">
          <SafetyCertificateOutlined
            style={{ fontSize: 24, marginRight: 12, color: "#bf0600" }}
          />
          <div>
            <h2 className="text-2xl font-bold mb-1">Security Features</h2>
            <p className="text-gray-600 m-0">
              Globally enable or disable CSE password-policy and 2FA features.
              All features are disabled by default.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : (
          <div className="space-y-3">
            {FEATURES.map((feature) => {
              const enabled = !!settings[feature.key];
              return (
                <div
                  key={feature.key}
                  className="flex items-center justify-between border rounded-lg px-4 py-3"
                  style={{
                    backgroundColor: enabled ? "#f6ffed" : "#fff7f6",
                    borderColor: enabled ? "#b7eb8f" : "#ffccc7",
                  }}
                >
                  <div className="flex items-start">
                    {enabled ? (
                      <CheckCircleFilled
                        style={{
                          color: "#52c41a",
                          fontSize: 18,
                          marginRight: 12,
                          marginTop: 3,
                        }}
                      />
                    ) : (
                      <CloseCircleFilled
                        style={{
                          color: "#ff4d4f",
                          fontSize: 18,
                          marginRight: 12,
                          marginTop: 3,
                        }}
                      />
                    )}
                    <div>
                      <div className="flex items-center">
                        <span className="font-medium mr-2">
                          {feature.title}
                        </span>
                        <Tag color={enabled ? "green" : "red"}>
                          {enabled ? "Enabled" : "Disabled"}
                        </Tag>
                      </div>
                      <div className="text-sm text-gray-500">
                        {feature.description}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {feature.policy}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    loading={!!updating[feature.key]}
                    onChange={(checked) => handleToggle(feature.key, checked)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default SecuritySettings;
